import axios from "axios";
import { jwtDecode } from "jwt-decode";

// Single source of truth for session/token handling in the vendor dashboard.
// Every place that reads, writes, or clears the token should go through here so
// there is exactly one logout path and one storage key.

const TOKEN_KEY = "access_token";
const REFRESH_KEY = "refresh_token";
const REFRESH_URL = "/mp/auth/refresh";
const LOGOUT_URL = "/mp/auth/logout";

// A BARE axios client (no interceptors) used only to hit the refresh/logout
// endpoints — prevents the response interceptor from recursing into itself.
const bareClient = axios.create({
  baseURL: (process.env.REACT_APP_BASE_URL || "") + "/",
});

// Auth-related keys cleared on logout. UI preferences (theme, layout, sidebar,
// animation) are intentionally left untouched so logging out doesn't reset the
// user's dashboard look & feel.
const AUTH_KEYS = [
  TOKEN_KEY,
  REFRESH_KEY,
  "login",
  "authenticated",
  "isFirstTime",
  "merchant-types",
  "profileURL",
  "auth0_profile",
  "Name",
];

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const setToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
};

export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

export const setRefreshToken = (token) => {
  if (token) localStorage.setItem(REFRESH_KEY, token);
};

// Persist both tokens after login/register/refresh, and announce the session so
// app-level providers (socket, notifications) can start immediately: login
// navigates via react-router without a page reload, so mount-time effects in
// providers above the router never re-run on their own.
export const setSession = (accessToken, refreshToken) => {
  setToken(accessToken);
  setRefreshToken(refreshToken);
  window.dispatchEvent(new Event("auth:session"));
};

export const getDecodedToken = () => {
  const token = getToken();
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch (err) {
    return null;
  }
};

// `exp` is in SECONDS (JWT standard); Date.now() is in ms. A token with no `exp`
// claim is treated as non-expiring — the server stays the source of truth via 401s.
export const isTokenExpired = () => {
  const decoded = getDecodedToken();
  if (!decoded) return true;
  if (!decoded.exp) return false;
  return decoded.exp * 1000 <= Date.now();
};

export const isAuthenticated = () => !!getToken() && !isTokenExpired();

export const clearSession = () => {
  AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
};

// Public (unguarded) pages — keep in sync with Route/AuthRoutes.jsx and the
// /login and /callback routes in Route/index.jsx. logout() must NEVER hard-reload
// while the user is on one of these: they are already where a logged-out user
// belongs, and reloading a public page that fired the logout would loop.
const PUBLIC_PATHS = [
  "/login",
  "/register",
  "/callback",
  "/privacy-policy",
  "/contact-us",
  "/pages/authentication/forget-password",
  "/merchant/details",
];

export const isPublicPage = () => {
  const path = window.location.pathname.toLowerCase().replace(/\/+$/, "");
  return PUBLIC_PATHS.some((p) => path === p || path.startsWith(p + "/"));
};

// Exchange the refresh token for a new access token (and rotated refresh token).
// Single-flight: concurrent 401s share ONE network refresh. Returns the new
// access token, or null if refresh failed (caller should then log out).
let refreshPromise = null;
export const refreshAccessToken = () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(null);

  if (!refreshPromise) {
    refreshPromise = bareClient
      .post(REFRESH_URL, { refreshToken })
      .then(({ data }) => {
        if (data?.token) {
          setSession(data.token, data.refreshToken || refreshToken);
          return data.token;
        }
        return null;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Single logout entry point. Best-effort revokes the refresh token server-side
// (so it can't be reused), clears the session, then hard-reloads ONCE so all
// per-user in-memory state (React contexts, caches) is wiped and the route
// guards (PrivateRoute) re-evaluate and send the user to the login page.
//
// The reload is strictly tied to ENDING A REAL SESSION. If there was no session
// to begin with (e.g. a stray 401 on a public page like /login), reloading
// would remount the page, re-fire the same request, and loop forever — so in
// that case this is a no-op. The `loggingOut` guard additionally collapses
// multiple concurrent 401s into a single reload.
let loggingOut = false;
export const logout = async () => {
  if (loggingOut) return;
  loggingOut = true;

  // Snapshot BEFORE clearing anything.
  const hadSession = !!getToken() || !!getRefreshToken();

  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await bareClient.post(LOGOUT_URL, { refreshToken }, { timeout: 4000 });
    } catch (e) {
      // ignore — we clear the session locally regardless
    }
  }

  clearSession();

  // Reload ONLY when a real session ended AND we're on a guarded page. On public
  // pages (login, register, forgot-password, …) there is nothing to re-route —
  // reloading there is at best pointless and at worst an infinite loop.
  if (!hadSession || isPublicPage()) {
    loggingOut = false;
    return;
  }

  window.location.reload();
};
